import React, { useState, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { TrendingUp } from "lucide-react";
import {
  COUNTRIES,
  BUILDING_TYPES,
  FINISH_LEVELS,
  US_REGIONS,
  CA_REGIONS,
} from "../mock";

const CostTrends = () => {
  const [country, setCountry] = useState("US");
  const [type, setType] = useState("single_family");
  const [size, setSize] = useState(2000);
  const [finish, setFinish] = useState("standard");

  const regions = country === "US" ? US_REGIONS : CA_REGIONS;
  const countryObj = COUNTRIES.find((c) => c.code === country);

  const data = useMemo(() => {
    const bt = BUILDING_TYPES.find((b) => b.id === type);
    const fl = FINISH_LEVELS.find((f) => f.id === finish);
    const fx = country === "CA" ? 1.36 : 1;
    const rows = regions.map((rg) => {
      const total = bt.baseRate * fl.mult * rg.mult * fx * Number(size || 0);
      return { name: rg.name, total };
    });
    const max = Math.max(...rows.map((r) => r.total));
    const min = Math.min(...rows.map((r) => r.total));
    return { rows, max, min };
  }, [country, type, size, finish, regions]);

  const fmt = (n) => `${countryObj.symbol}${Math.round(n).toLocaleString("en-US")}`;

  return (
    <section id="trends" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-semibold uppercase tracking-wider text-[#DC2626]">
            Compare markets
          </span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-700 tracking-tight text-[#0A2540]">
            How your build shifts across regions
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            The same project can cost thousands more depending on where you build. See it at a glance.
          </p>
        </div>

        <div className="mt-12 rounded-2xl border border-slate-200 shadow-sm p-7">
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-700">Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Building type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BUILDING_TYPES.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Size (sq ft)</Label>
              <Input type="number" value={size} min={100} onChange={(e) => setSize(e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-slate-700">Finish level</Label>
              <Select value={finish} onValueChange={setFinish}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FINISH_LEVELS.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {data.rows.map((r) => {
              const pct = (r.total / data.max) * 100;
              const isMax = r.total === data.max;
              const isMin = r.total === data.min;
              return (
                <div key={r.name}>
                  <div className="flex justify-between items-center text-sm mb-1.5">
                    <span className="font-medium text-[#0A2540] flex items-center gap-2">
                      {r.name}
                      {isMax && <span className="text-xs rounded-full bg-red-50 text-[#DC2626] px-2 py-0.5">Priciest</span>}
                      {isMin && <span className="text-xs rounded-full bg-blue-50 text-[#1D4ED8] px-2 py-0.5">Best value</span>}
                    </span>
                    <span className="font-semibold text-[#0A2540]">{fmt(r.total)}</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-3 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: isMax ? "#DC2626" : isMin ? "#1D4ED8" : "#60a5fa" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <TrendingUp className="h-4 w-4 text-[#1D4ED8]" />
            Spread of {fmt(data.max - data.min)} between the priciest and best-value region for this build.
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostTrends;
